import { AsyncPipe, DatePipe } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FollowUpLinkType } from '@app/modules/follow-ups/follow-ups.types';
import { FollowUpsUtilsService } from '@app/modules/follow-ups/services/follow-ups-utils.service';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { GetSamplesListAction } from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import { Kit, KitCollectorType, Partner, Run } from '@core/core.types';
import { LoadKitById } from '@core/store/kits/kits.actions';
import { KitsState } from '@core/store/kits/kits.state';
import { LoadPartnerById } from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { RunState } from '@core/store/runs/runs.state';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { DisplayNamePipe } from '@shared/pipes/display-name.pipe';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-kit-details',
  imports: [
    AsyncPipe,
    DatePipe,
    TranslatePipe,
    NgbTooltip,
    RequirePermissionDirective,
    DisplayNamePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './kit-details.component.html',
})
export class KitDetailsComponent implements OnInit {
  protected modalRef = inject(NgbActiveModal);
  protected sampleUtils = inject(SampleUtilsService);
  protected followUpsUtils = inject(FollowUpsUtilsService);
  protected coreUtilsService = inject(CoreUtilsService);
  private store = inject(Store);

  @Input() kitId;

  kit$: Observable<Kit>;
  runs$: Observable<Run[]>;
  partner$: Observable<Partner>;
  collectorTypes$: Observable<KitCollectorType[]>;

  data$: Observable<{ kit: Kit; runs: Run[]; partner: Partner }>;

  runs = signal<string>('');

  ngOnInit() {
    this.kit$ = this.store.dispatch(new LoadKitById(this.kitId)).pipe(
      switchMap(() => this.store.select(KitsState.getKitById)),
      map((getById) => getById(this.kitId))
    );

    this.runs$ = this.kit$.pipe(
      switchMap((kit) => this.store.select(RunState.getRunsByKitId)),
      map((getByKitId) => getByKitId(this.kitId)),
      tap((runs) => this.runs.set(runs.map((r) => r.name).join(', ')))
    );

    this.partner$ = this.kit$.pipe(
      switchMap((kit) => {
        if (kit?.partnerId) {
          return this.store.dispatch(new LoadPartnerById(kit.partnerId)).pipe(
            switchMap(() => this.store.select(PartnersState.getPartnerById)),
            map((getById) => getById(kit.partnerId))
          );
        }
        return of(null);
      })
    );

    this.collectorTypes$ = this.store.select(KitsState.getKitCollectorTypes);

    this.data$ = combineLatest([this.kit$, this.runs$, this.partner$]).pipe(
      map(([kit, runs, partner]) => ({ kit, runs, partner }))
    );
  }

  protected openSampleDetails(kitId: string) {
    this.store
      .dispatch(new GetSamplesListAction({ kitIds: [kitId] }, {}, null, false))
      .pipe(
        switchMap(() => this.store.select(SampleState.getSampleByKitId)),
        map((getByKitId) => getByKitId(kitId)),
        first()
      )
      .subscribe((sample) => {
        this.sampleUtils.openDetails(sample.id);
      });
  }

  protected addFollowUp(kitId: string) {
    this.followUpsUtils.openCreateFollowUps([kitId], FollowUpLinkType.Kit, [
      { id: kitId },
    ]);
  }
}
